import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, Paperclip } from "lucide-react";
import { http, API } from "../../lib/api";

const STATUSES = ["New", "Contacted", "Quoted", "Won", "Lost"];

const StatusPill = ({ status }) => {
  const tone = {
    New: "bg-[#0F4C81] text-white",
    Contacted: "bg-[#111111] text-white",
    Quoted: "bg-[#7A7F85] text-white",
    Won: "bg-[#1463A5] text-white",
    Lost: "bg-[#F5F5F2] text-[#7A7F85] border border-[#111111]/10",
  }[status];
  return (
    <span className={`px-3 py-1.5 text-[0.625rem] font-bold uppercase tracking-[0.14em] ${tone}`}>
      {status}
    </span>
  );
};

export const EnquiriesPanel = () => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);
  const [note, setNote] = useState("");

  const load = () =>
    http
      .get("/admin/enquiries", { params: filter ? { status: filter } : {} })
      .then((r) => setRows(r.data))
      .catch(() => toast.error("Could not load enquiries."));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const setStatus = async (id, status) => {
    await http.patch(`/admin/enquiries/${id}`, { status });
    toast.success(`Marked as ${status}`);
    load();
  };

  const addNote = async (id) => {
    if (!note.trim()) return;
    const { data } = await http.patch(`/admin/enquiries/${id}`, { note });
    setNote("");
    setOpen(data);
    toast.success("Note added");
    load();
  };

  const remove = async (id) => {
    await http.delete(`/admin/enquiries/${id}`);
    setOpen(null);
    toast.success("Enquiry deleted");
    load();
  };

  return (
    <div data-testid="admin-enquiries">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          {["", ...STATUSES].map((s) => (
            <button
              key={s || "all"}
              data-testid={`enquiry-filter-${s || "all"}`}
              onClick={() => setFilter(s)}
              className={`border px-3.5 py-2 text-[0.625rem] font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
                filter === s
                  ? "border-[#0F4C81] bg-[#0F4C81] text-white"
                  : "border-[#111111]/15 text-[#3B3B3B] hover:border-[#0F4C81]"
              }`}
            >
              {s || "All"}
            </button>
          ))}
        </div>
        <a
          href={`${API}/admin/enquiries/export`}
          data-testid="enquiry-export"
          className="inline-flex items-center gap-2 border border-[#111111]/15 px-4 py-2.5 text-[0.625rem] font-bold uppercase tracking-[0.14em] text-[#111111] transition-colors duration-300 hover:border-[#0F4C81] hover:text-[#0F4C81]"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </a>
      </div>

      <div className="mt-8 overflow-x-auto border border-[#111111]/10 bg-white">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead>
            <tr className="border-b border-[#111111]/10 bg-[#F5F5F2]">
              {["Received", "Name", "Facility", "City", "Products", "Status", ""].map((h) => (
                <th key={h} className="mako-label px-5 py-4 text-[#7A7F85]">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                data-testid={`enquiry-row-${r.id}`}
                className="cursor-pointer border-b border-[#111111]/8 transition-colors duration-200 hover:bg-[#F5F5F2]"
                onClick={() => setOpen(r)}
              >
                <td className="px-5 py-4 text-xs text-[#7A7F85]">
                  {new Date(r.created_at).toLocaleDateString("en-GB")}
                </td>
                <td className="px-5 py-4 font-semibold text-[#111111]">{r.full_name}</td>
                <td className="px-5 py-4 text-[#3B3B3B]">{r.organisation || "—"}</td>
                <td className="px-5 py-4 text-[#3B3B3B]">{r.city || "—"}</td>
                <td className="px-5 py-4 text-xs text-[#3B3B3B]">
                  {(r.products || []).join(", ") || "—"}
                </td>
                <td className="px-5 py-4">
                  <StatusPill status={r.status} />
                </td>
                <td className="px-5 py-4 text-right">
                  {(r.attachments || []).length > 0 && (
                    <Paperclip className="inline h-3.5 w-3.5 text-[#7A7F85]" />
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-5 py-10 text-center text-sm text-[#7A7F85]">
                  No enquiries yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-[#111111]/50"
          onClick={() => setOpen(null)}
        >
          <div
            data-testid="enquiry-drawer"
            onClick={(e) => e.stopPropagation()}
            className="h-full w-full max-w-xl overflow-y-auto bg-white p-8 lg:p-10"
          >
            <div className="flex items-start justify-between gap-6">
              <div>
                <p className="mako-label text-[#0F4C81]">{open.source}</p>
                <h3 className="mt-3 font-display text-2xl font-bold text-[#111111]">
                  {open.full_name}
                </h3>
                <p className="mt-2 text-sm text-[#3B3B3B]">
                  {open.organisation} · {open.city} {open.state}
                </p>
              </div>
              <button
                onClick={() => remove(open.id)}
                data-testid="enquiry-delete"
                className="text-[#7A7F85] transition-colors duration-300 hover:text-[#b3261e]"
                aria-label="Delete enquiry"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  data-testid={`enquiry-status-${s}`}
                  onClick={() => {
                    setStatus(open.id, s);
                    setOpen({ ...open, status: s });
                  }}
                  className={`border px-3.5 py-2 text-[0.625rem] font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
                    open.status === s
                      ? "border-[#0F4C81] bg-[#0F4C81] text-white"
                      : "border-[#111111]/15 text-[#3B3B3B] hover:border-[#0F4C81]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            <dl className="mt-9 divide-y divide-[#111111]/10 text-sm">
              {[
                ["Phone", open.phone],
                ["Email", open.email],
                ["Facility type", open.facility_type],
                ["Sports", (open.sports || []).join(", ")],
                ["Products", (open.products || []).join(", ")],
                ["Area", open.area],
                ["Timeline", open.timeline],
                ["Message", open.message],
              ].map(([k, v]) => (
                <div key={k} className="py-3.5">
                  <dt className="mako-label text-[#7A7F85]">{k}</dt>
                  <dd className="mt-2 text-[#111111]">{v || "—"}</dd>
                </div>
              ))}
            </dl>

            {(open.attachments || []).length > 0 && (
              <div className="mt-8">
                <p className="mako-label text-[#7A7F85]">Attachments</p>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {open.attachments.map((a) => {
                    const [name, data] = a.split("::");
                    return (
                      <a key={name} href={data} target="_blank" rel="noopener noreferrer">
                        <img src={data} alt={name} className="aspect-square w-full object-cover" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-9 border-t border-[#111111]/10 pt-7">
              <p className="mako-label text-[#7A7F85]">Notes</p>
              <ul className="mt-4 space-y-3">
                {(open.notes || []).map((n, i) => (
                  <li key={i} className="border-l-2 border-[#0F4C81] pl-4 text-sm text-[#3B3B3B]">
                    {n.text}
                    <span className="mt-1 block text-xs text-[#7A7F85]">
                      {new Date(n.at).toLocaleString("en-GB")} · {n.by}
                    </span>
                  </li>
                ))}
              </ul>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="enquiry-note-input"
                placeholder="Add a note"
                className="mt-5 min-h-[90px] w-full border border-[#111111]/15 px-4 py-3 text-sm focus:border-[#0F4C81] focus:outline-none"
              />
              <button
                onClick={() => addNote(open.id)}
                data-testid="enquiry-note-save"
                className="mt-3 bg-[#111111] px-6 py-3 text-[0.625rem] font-bold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-[#3B3B3B]"
              >
                Save note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
